<template>
    <div :id="id" class="donut-svg" :class="id" :style="`width: ${size}`">
        <svg width="100%" height="100%" viewBox="0 0 40 40" class="donut">
            <circle
                :stroke-width="strokeWidth"
                class="donut-hole"
                cx="20"
                cy="20"
                r="15.91549430918954"
                fill="transparent"
            ></circle>
            <circle
                class="donut-ring"
                cx="20"
                cy="20"
                r="15.91549430918954"
                fill="transparent"
                :stroke-width="strokeWidth"
                :stroke="ringColor"
            ></circle>
            <circle
                class="donut-segment"
                cx="20"
                cy="20"
                r="15.91549430918954"
                fill="transparent"
                :stroke-width="strokeWidth"
                :stroke="color"
                stroke-dasharray="69 31"
                stroke-dashoffset="25"
            ></circle>
            <g class="donut-text">
                <g class="arrow donut-up-arrow" transform="scale(.15) translate(115 55)">
                    <path d="M18 0L35.3205 30H0.679491L18 0Z" :fill="upColor" />
                </g>
                <text y="50%" x="50%" transform="translate(0, 4)">
                    <tspan
                        text-anchor="middle"
                        class="donut-percent"
                        :fill="color"
                        :style="`font-size: ${fontSize}; font-weight: ${fontWeight}; transform: translateY(${fontSize});`"
                    >
                        {{ percentFirst }}
                    </tspan>
                    <tspan
                        text-anchor="middle"
                        class="donut-percent-decimal"
                        :fill="color"
                        :style="`font-size: ${fontSizeSecond}; font-weight: ${fontWeight}; transform: translateY(${fontSizeSecond})`"
                    >
                        {{ percentDecimal }}
                    </tspan>
                </text>
                <g class="arrow donut-down-arrow" transform="scale(.15) translate(115 175)">
                    <path d="M18 30L0.679492 -3.26266e-06L35.3205 -2.34249e-07L18 30Z" :fill="downColor" />
                </g>
            </g>
        </svg>
    </div>
</template>

<script>
import gsap from 'gsap'

export default {
    props: {
        id: {
            type: String,
            default: 'chart-radial'
        },
        percent: {
            type: Number,
            default: 80
        },
        size: {
            type: String,
            default: '100%'
        },
        color: {
            type: String,
            default: '#ffffff'
        },
        ringColor: {
            type: String,
            default: 'rgba(255,255,255,0.5)'
        },
        fontSize: {
            type: String,
            default: '.75em'
        },
        fontSizeSecond: {
            type: String,
            default: '.55em'
        },
        strokeWidth: {
            type: String,
            default: '3.5'
        },
        fontWeight: {
            type: String,
            default: '400'
        },
        isShowArrowUp: {
            type: Boolean,
            default: false
        },
        isShowArrowDown: {
            type: Boolean,
            default: false
        },
        upColor: {
            type: String,
            default: '#009C1C'
        },
        downColor: {
            type: String,
            default: '#E40001'
        },
        showAnimate: {
            type: Boolean,
            default: false
        },
        isAnimateOnMount: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isAnimated: false,
            percentFirst: 0,
            percentDecimal: ''
        }
    },

    computed: {},

    watch: {
        id() {
            gsap.set(`.${this.id} .donut-segment`, { strokeDasharray: `0, 100` })
            gsap.set(`.${this.id} .donut-down-arrow, .${this.id} .donut-up-arrow`, { y: 20, opacity: 0 })

            if (!this.isAnimated) {
                gsap.set(`.${this.id}.donut-svg, .${this.id} .donut-percent, .${this.id} .donut-percent-decimal`, {
                    opacity: 0
                })
                this.animation()
            }
        },

        showAnimate() {
            gsap.set(`.${this.id} .donut-segment`, { strokeDasharray: `0, 100` })
            gsap.set(`.${this.id} .donut-down-arrow, .${this.id} .donut-up-arrow`, { y: 20, opacity: 0 })

            if (!this.isAnimated) {
                gsap.set(`.${this.id}.donut-svg, .${this.id} .donut-percent, .${this.id} .donut-percent-decimal`, {
                    opacity: 0
                })
                this.animation()
            }
        }
    },

    mounted() {
        gsap.set(`.${this.id} .donut-segment`, { strokeDasharray: `0, 100` })
        gsap.set(`.${this.id} .donut-down-arrow, .${this.id} .donut-up-arrow`, { y: 20, opacity: 0 })

        if (this.isAnimateOnMount) {
            gsap.set(`.${this.id}.donut-svg, .${this.id} .donut-percent, .${this.id} .donut-percent-decimal`, {
                opacity: 0
            })

            this.animation()
        }
    },

    methods: {
        animation() {
            gsap.set('.arrow', { opacity: 0 })
            gsap.to(`.${this.id}.donut-svg`, { duration: 0.5, opacity: 1, ease: 'power4.out' })
            gsap.to(`.${this.id} .donut-segment`, {
                duration: 2,
                strokeDasharray: `${this.percent}, ${100 - this.percent}`,
                ease: 'power4.out'
            })
            gsap.to(`.${this.id} .donut-percent, .donut-percent-decimal`, {
                duration: 1,
                opacity: 1,
                ease: 'power4.out',
                delay: 0.5
            })

            if (this.isShowArrowUp) {
                gsap.to(`.${this.id} .donut-up-arrow`, {
                    duration: 0.5,
                    y: 9,
                    opacity: 1,
                    ease: 'power4.out',
                    delay: 0.5
                })
            }

            if (this.isShowArrowDown) {
                gsap.to(`.${this.id} .donut-down-arrow`, {
                    duration: 0.5,
                    y: 26,
                    opacity: 1,
                    ease: 'power4.out',
                    delay: 0.5
                })
            }

            this.getPercentFirst()
            this.getPercentDecimal()
        },

        getPercentFirst() {
            let percent = this.percent.toFixed(1).split('.')[0]
            this.percentFirst = `${percent}`
        },

        getPercentDecimal() {
            let decimal = this.percent.toFixed(1).split('.')[1]
            this.percentDecimal = parseFloat(decimal) > 0 ? `,${decimal}` : ''
        }
    }
}
</script>

<style lang="scss" scoped>
.donut-svg {
    margin: 0 auto;

    .donut-segment {
        transform-origin: center;
    }

    .donut-text {
        font-family: 'Open Sans', sans-serif;
    }

    .donut-percent,
    .donut-percent-decimal {
        line-height: 1;
        font-weight: bold;
    }
}
</style>
